import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { clearAllUserCaches, clearCache } from '@/lib/cacheUtils';
import { clearPlanLimitsCache } from '@/hooks/usePlanLimits';

type AppRole = 'admin' | 'moderator' | 'user' | 'pro' | 'business' | 'enterprise' | 'support_agent' | 'inventory_manager' | 'content_moderator';

interface Profile {
  id: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  avatar_color: string | null;
  pricing_preference: 'rent' | 'buy' | 'hidden' | null;
  favorite_rental_house_id: string | null;
  preferred_currency: string | null;
  created_at: string;
  updated_at: string;
  ai_message_limit_override: number | null;
}

interface Subscription {
  isSubscribed: boolean;
  productId: string | null;
  priceId: string | null;
  subscriptionEnd: string | null;
  subscriptionStart: string | null;
  isAdminGranted?: boolean;
  grantedRole?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  isLoading: boolean;
  isAdmin: boolean;
  isPremium: boolean;
  pricingPreference: 'rent' | 'buy' | 'hidden';
  subscription: Subscription | null;
  updatePricingPreference: (pref: 'rent' | 'buy' | 'hidden') => Promise<void>;
  updateFavoriteRentalHouse: (houseId: string | null) => Promise<void>;
  updatePreferredCurrency: (currency: string) => Promise<void>;
  updateAvatarColor: (color: string | null) => Promise<void>;
  updateAvatarUrl: (url: string | null) => Promise<void>;
  updateDisplayName: (displayName: string | null) => Promise<void>;
  checkSubscription: () => Promise<void>;
  openCustomerPortal: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    username: string,
  ) => Promise<{ error: Error | null; data: { user: User | null } | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkSubscription = async () => {
    if (!session) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        console.error('Error checking subscription:', error);
        return;
      }
      
      // Clear plan limits cache when subscription changes
      clearPlanLimitsCache();
      
      setSubscription({
        isSubscribed: data.subscribed || false,
        productId: data.product_id || null,
        priceId: data.price_id || null,
        subscriptionEnd: data.subscription_end || null,
        subscriptionStart: data.subscription_start || null,
        isAdminGranted: data.is_admin_granted || false,
        grantedRole: data.granted_role || undefined,
      });
    } catch (err) {
      console.error('Failed to check subscription:', err);
    }
  };

  const openCustomerPortal = async () => {
    if (!session) return;
    
    try {
      const { data, error } = await supabase.functions.invoke('customer-portal', {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });
      
      if (error) {
        console.error('Error opening customer portal:', error);
        return;
      }
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to open customer portal:', err);
    }
  };

  const fetchUserData = async (userId: string) => {
    try {
      // Fetch profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
      } else if (profileData) {
        setProfile(profileData as Profile);
      }

      // Fetch roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);
      
      if (rolesError) {
        console.error('Error fetching roles:', rolesError);
      } else if (rolesData) {
        const newRoles = rolesData.map(r => r.role as AppRole);
        // Clear plan limits cache when roles change
        clearPlanLimitsCache();
        setRoles(newRoles);
      }
    } catch (error) {
      console.error('Unexpected error fetching user data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer Supabase calls with setTimeout to prevent deadlock
        if (session?.user) {
          setTimeout(() => {
            fetchUserData(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setRoles([]);
          setSubscription(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserData(session.user.id);
      } else {
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
    email: string,
    password: string,
    username: string,
  ) => {
    const redirectUrl = `${window.location.origin}/`;
    
    try {
      // Use custom signup edge function to avoid Supabase's default email
      const { data, error } = await supabase.functions.invoke('signup-user', {
        body: {
          email,
          password,
          username,
          redirectTo: redirectUrl,
        },
      });
      
      if (error) {
        return { error: error as Error, data: null };
      }
      
      if (data?.error) {
        return { error: new Error(data.error), data: null };
      }
      
      if (data?.user) {
        return { 
          error: null, 
          data: { 
            user: {
              ...data.user,
              // Mark that verification is needed
              identities: [],
              email_confirmed_at: null,
            }
          } 
        };
      }
      
      return { error: new Error('Failed to create user'), data: null };
    } catch (err: any) {
      return { error: err as Error, data: null };
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    return { error: error as Error | null };
  };

  const signOut = async () => {
    console.info('[Auth] Starting sign out...');
    
    // STEP 1: Clear Supabase auth storage FIRST (before any async operations)
    // This prevents the session from being restored on page reload
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    const supabaseStorageKey = `sb-${projectId}-auth-token`;
    localStorage.removeItem(supabaseStorageKey);
    sessionStorage.removeItem(supabaseStorageKey);
    
    // Also clear any other auth-related keys (catches all variants)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') && key.includes('-auth-')) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-') && key.includes('-auth-')) {
        sessionStorage.removeItem(key);
      }
    });
    
    console.info('[Auth] Cleared auth storage');
    
    try {
      // STEP 2: Attempt server-side sign-out with timeout
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Sign out timeout')), 3000)
      );
      
      await Promise.race([
        supabase.auth.signOut({ scope: 'global' }),
        timeoutPromise
      ]);
      
      console.info('[Auth] Global sign out succeeded');
    } catch (err) {
      console.warn('[Auth] Global sign out failed/timed out:', err);
      
      // Fallback to local sign-out
      try {
        await supabase.auth.signOut({ scope: 'local' });
      } catch (localErr) {
        console.error('[Auth] Local sign out also failed:', localErr);
      }
    }
    
    // STEP 3: Clear all app caches using centralized utility
    clearAllUserCaches();
    clearPlanLimitsCache(); // Also clear React Query cache
    
    // STEP 4: Clear React state
    setUser(null);
    setSession(null);
    setProfile(null);
    setRoles([]);
    setSubscription(null);
    
    // STEP 5: Hard redirect with small delay to ensure storage is cleared
    setTimeout(() => {
      window.location.href = '/';
    }, 50);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;
    
    // Use branded password reset email via edge function
    try {
      const { error } = await supabase.functions.invoke('send-password-reset-email', {
        body: {
          email,
          redirectTo: redirectUrl,
        }
      });
      
      // Always return success for security (don't reveal if email exists)
      return { error: null };
    } catch (err) {
      console.error('Failed to send password reset email:', err);
      // Still return success to not reveal if email exists
      return { error: null };
    }
  };

  const isAdmin = roles.includes('admin');
  const isPremium = roles.includes('pro') || roles.includes('business') || roles.includes('admin');
  const pricingPreference: 'rent' | 'buy' | 'hidden' = profile?.pricing_preference || 'rent';

  const updatePricingPreference = async (pref: 'rent' | 'buy' | 'hidden') => {
    if (!user || !profile) return;
    
    // Optimistic update - update UI immediately
    const previousPref = profile.pricing_preference;
    setProfile({ ...profile, pricing_preference: pref });
    
    // Persist to database in background
    const { error } = await supabase
      .from('profiles')
      .update({ pricing_preference: pref })
      .eq('id', user.id);
    
    // Rollback on error
    if (error) {
      setProfile({ ...profile, pricing_preference: previousPref });
      console.error('Failed to update pricing preference:', error);
    }
  };

  const updateFavoriteRentalHouse = async (houseId: string | null) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ favorite_rental_house_id: houseId })
      .eq('id', user.id);
    
    if (!error && profile) {
      setProfile({ ...profile, favorite_rental_house_id: houseId });
    }
  };

  const updatePreferredCurrency = async (currency: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ preferred_currency: currency })
      .eq('id', user.id);
    
    if (!error && profile) {
      setProfile({ ...profile, preferred_currency: currency });
    }
  };

  const updateAvatarColor = async (color: string | null) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_color: color })
      .eq('id', user.id);
    
    if (!error && profile) {
      setProfile({ ...profile, avatar_color: color });
    }
  };

  const updateAvatarUrl = async (url: string | null) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: url })
      .eq('id', user.id);
    
    if (!error && profile) {
      setProfile({ ...profile, avatar_url: url });
    }
  };

  const updateDisplayName = async (displayName: string | null) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('profiles')
      .update({ display_name: displayName })
      .eq('id', user.id);
    
    if (error) {
      throw error;
    }
    
    if (profile) {
      setProfile({ ...profile, display_name: displayName });
    }
  };

  // Check subscription on session changes
  useEffect(() => {
    if (session && user) {
      checkSubscription();
    }
  }, [session, user]);

  // Real-time subscription for role changes (e.g., when admin updates user's plan)
  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`user-role-changes-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
          filter: `user_id=eq.${user.id}`,
        },
        async () => {
          console.info('[Auth] Role change detected, refreshing roles...');
          
          // Re-fetch roles from database
          const { data: rolesData, error } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', user.id);
          
          if (!error && rolesData) {
            const newRoles = rolesData.map(r => r.role as AppRole);
            clearPlanLimitsCache();
            clearCache('LINKED_RENTAL_PRICES');
            setRoles(newRoles);
            console.info('[Auth] Roles refreshed:', newRoles);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      profile,
      roles,
      isLoading,
      isAdmin,
      isPremium,
      pricingPreference,
      subscription,
      updatePricingPreference,
      updateFavoriteRentalHouse,
      updatePreferredCurrency,
      updateAvatarColor,
      updateAvatarUrl,
      updateDisplayName,
      checkSubscription,
      openCustomerPortal,
      signUp,
      signIn,
      signOut,
      resetPassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
